/** Strict public snapshot decoding before request-owned UI progress can change. */
import { z } from 'zod';
import type { ChatPlanSnapshot } from '../../models/ChatPlan';

const id = z.string().regex(/^[a-z][a-z0-9_-]{0,23}$/);
const schema = z.object({
  runId: z.string().uuid(), revision: z.number().int().min(1),
  status: z.enum(['running', 'waiting_input', 'completed', 'blocked', 'stopped']),
  steps: z.array(z.object({
    id, label: z.string().min(1).max(160), kind: z.enum(['read', 'answer', 'draft']),
    dependencies: z.array(id).max(5),
    status: z.enum(['pending', 'in_progress', 'completed', 'waiting_input', 'blocked', 'stopped', 'not_needed']),
    outcome: z.enum(['evidence', 'empty', 'partial', 'failed', 'answered', 'draft_prepared', 'budget']).optional()
  }).strict()).min(1).max(10)
}).strict().superRefine((plan, context) => {
  const ids = new Set(plan.steps.map(step => step.id));
  if (ids.size !== plan.steps.length || plan.steps.some(step => step.dependencies.some(dep => !ids.has(dep))) ||
    plan.status === 'completed' && plan.steps.some(step => !['completed', 'not_needed'].includes(step.status)))
    context.addIssue({ code: 'custom', message: 'Inconsistent plan snapshot' });
});
export function parseChatPlan(value: unknown): ChatPlanSnapshot | null {
  const parsed = schema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** Optional outer-event metadata must never invalidate an otherwise valid progress plan. */
export function parseChatTitle(value: unknown): string | undefined {
  const parsed = z.string().trim().min(1).max(80).regex(/^[^\r\n\t]+$/).safeParse(value);
  return parsed.success ? parsed.data : undefined;
}
